import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { CFG_MAX_STEPS, CFG_MIN_STEP_MS } from '../../protocol/constants.ts';
import type { JigStep } from '../../protocol/types.ts';
import { computeDisruption, formatPxPerSec } from '../../domain/disruption.ts';
import type { Draft } from '../../domain/draft.ts';
import { PatternKind } from '../../protocol/constants.ts';
import { resampleTrace, stepsCycleMs, stepsPathPx } from './resample.ts';
import { usePointerRecorder } from './usePointerRecorder.ts';

interface Props {
  /** The device must be parked before recording, or its own motion pollutes the trace. */
  deviceStopped: boolean;
  onEnsureStopped: () => Promise<void>;
  onUse: (steps: JigStep[]) => void;
  compact?: boolean;
}

const LOOP_PRESETS = [
  ['5 s', 5000],
  ['10 s', 10_000],
  ['20 s', 20_000],
  ['60 s', 60_000],
] as const;

export function Recorder({ deviceStopped, onEnsureStopped, onUse, compact = false }: Props) {
  const rec = usePointerRecorder();
  const [loopMs, setLoopMs] = useState<number | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const result = useMemo(
    () => (rec.samples.length > 0 ? resampleTrace(rec.samples, loopMs === undefined ? {} : { loopMs }) : null),
    [rec.samples, loopMs],
  );

  /* The device jiggles on its own. Its motion would land in the trace, so recording
     refuses to start until it is confirmed parked -- asserting the state, not merely
     sending STOP and hoping. */
  const onStart = useCallback(
    async (target: HTMLElement) => {
      setBusy(true);
      try {
        // Parking is handed to `capture()` rather than awaited here, so that it runs
        // AFTER the pointer lock is held. `requestPointerLock()` needs a user gesture,
        // and awaiting a STOP round-trip before calling it can push the request past
        // the end of the activation window, where the browser silently refuses it.
        await rec.capture(target, onEnsureStopped);
      } finally {
        setBusy(false);
      }
    },
    [onEnsureStopped, rec],
  );

  const preview = result ? computeDisruption(emptyDraftFor(result.steps)) : null;

  return (
    <section className="panel">
      <h2>{compact ? 'Record' : 'Record movement, clicks and drags'}</h2>

      {!compact && <p className="dim" style={{ marginBottom: 12 }}>
        Move, click, or hold a button and drag. Left, right and middle buttons are
        captured, including stationary clicks. The pointer is <strong>locked</strong>{' '}
        while recording so the screen edge does not cut off your trace.
      </p>}

      <p className="dim" style={{ marginBottom: 12 }}>
        {compact ? 'Space to stop' : <>
          <strong>Press the space bar to stop.</strong> Clicks are recorded, not stop
          commands. The initial click that starts recording is not included. Playback
          uses relative movement from wherever the cursor starts, not screen coordinates.
          Scrolling and extra mouse buttons are not recorded.
        </>}
      </p>

      {!deviceStopped && rec.phase === 'idle' && (
        <p className="warn">
          {compact ? 'The device will be parked before recording.' : <>
            The device is not parked. Starting a recording will send <code>STOP</code>{' '}
            first, because the device's own cursor movement would otherwise be captured as
            part of your drawing.
          </>}
        </p>
      )}

      {/* The SAME connected element must own the lock in every phase. */}
      <RecordTarget
        onStart={onStart}
        disabled={busy}
        recording={rec.phase === 'recording'}
        hasSamples={rec.samples.length > 0 || rec.sampleCount > 0}
        failed={rec.phase === 'error'}
        compact={compact}
      >
        {rec.phase === 'recording' && (
          <>
            <p className="recording__label">
              {compact ? rec.isLocked ? 'Recording' : 'Pointer not locked' : <>Recording{rec.isLocked ? ' — pointer locked' : ' — NOT locked'}</>}
            </p>
            <p className="recording__time">{(rec.elapsedMs / 1000).toFixed(1)} s</p>
            <p className="dim">{rec.sampleCount} {compact ? 'events' : 'samples'}</p>
            {!compact && <p className="dim">Press space to stop. Clicks and drags are being recorded.</p>}
          </>
        )}
      </RecordTarget>

      {rec.error && <p className="bad">{compact ? compactRecorderError(rec.error) : rec.error}</p>}

      {result && rec.phase !== 'recording' && (
        <>
          {!compact && <dl className="facts">
            <dt>Captured</dt>
            <dd>
              {(result.traceMs / 1000).toFixed(2)} s, {rec.samples.length} samples,{' '}
              {result.tracePathPx.toFixed(0)} px of travel
            </dd>
            <dt>Resampled</dt>
            <dd>
              {result.requiredSteps} steps over {result.playbackMs} ms
              {result.splitSteps > 0 && ` (${result.splitSteps} from splitting fast strokes)`}
              {result.mergedSteps > 0 && ` (${result.mergedSteps} still steps merged)`}
            </dd>
          </dl>}

          {result.truncated && (
            <p className="bad">
              {compact ? `Needs ${result.requiredSteps} steps; limit ${CFG_MAX_STEPS}. Record a shorter or smaller gesture.` : <>
                This recording needs {result.requiredSteps} steps; the device holds only
                {' '}{CFG_MAX_STEPS}. Nothing will be uploaded: click/drag boundaries must
                not be dropped. Record a shorter or smaller gesture.
              </>}
            </p>
          )}

          {result.timingAdjustmentMs > 0 && (
            <p className="warn">
              {compact ? `Playback extended by ${result.timingAdjustmentMs} ms to keep every action.` : <>
                Playback needs {result.timingAdjustmentMs} ms extra for report splitting,
                button boundaries and the final release. The device cannot emit faster
                than one report every {CFG_MIN_STEP_MS} ms.
              </>}
            </p>
          )}

          {!compact && preview && !result.truncated && (
            <p className="dim">
              Plays back at <strong>{formatPxPerSec(preview.pxPerSec)}</strong> —{' '}
              {stepsPathPx(result.steps)} px per loop of {(stepsCycleMs(result.steps) / 1000).toFixed(1)} s.
            </p>
          )}

          <div className="row">
            <span className="dim">Playback timing:</span>
            <button
              type="button"
              className={`btn btn--small ${loopMs === undefined ? 'btn--on' : ''}`}
              onClick={() => setLoopMs(undefined)}
            >
              Original timing
            </button>
            {LOOP_PRESETS.map(([label, ms]) => (
              <button
                key={label}
                type="button"
                className={`btn btn--small ${loopMs === ms ? 'btn--on' : ''}`}
                onClick={() => setLoopMs(ms)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={result.truncated || result.steps.length === 0}
              onClick={() => onUse(result.steps)}
            >
              {compact ? 'Use recording' : 'Use as pattern'}
            </button>
            <button type="button" className="btn btn--small" onClick={rec.discard}>
              Discard
            </button>
          </div>
        </>
      )}

      {!compact && <p className="dim" style={{ marginTop: 12 }}>
        Original timing is the default; the optional durations scale pauses and
        button holds too. Dense movement is grouped without crossing button changes;
        very fast events may take longer at the device's {CFG_MIN_STEP_MS} ms floor.
        Each loop finishes with all buttons released. The macro repeats for the run
        duration configured on the Pattern tab.
      </p>}
    </section>
  );
}

interface RecordTargetProps {
  onStart: (target: HTMLElement) => Promise<void>;
  disabled: boolean;
  recording: boolean;
  children: ReactNode;
  hasSamples: boolean;
  /** A previous attempt was refused; the button has to stay so it can be retried. */
  failed: boolean;
  compact: boolean;
}

/**
 * The click target doubles as the pointer-lock element. Pointer Lock requires a user
 * gesture, so the element has to be something the user actually clicked.
 */
function RecordTarget({ onStart, disabled, recording, children, hasSamples, failed, compact }: RecordTargetProps) {
  return (
    <div
      className={recording ? 'recording' : 'record-target'}
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      onClick={(e) => {
        if (!disabled && !recording) void onStart(e.currentTarget);
      }}
      onContextMenu={(e) => { if (recording) e.preventDefault(); }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && !recording && !e.repeat) {
          e.preventDefault();
          void onStart(e.currentTarget);
        }
      }}
    >
      {recording ? children : disabled
        ? compact ? 'Parking…' : 'Parking the device…'
        : failed
          ? compact ? 'Try again' : 'Click to try again'
          : hasSamples
            ? compact ? 'Record again' : 'Click to record again'
            : compact ? 'Start recording' : 'Click here, then draw'}
    </div>
  );
}

/** Shorten only our explanatory copy; keep the browser's quoted refusal verbatim. */
function compactRecorderError(error: string): string {
  const refusal = error.match(/^The browser refused to lock the pointer\.(?: It reported: “[\s\S]*”.)?/);
  if (refusal) return `${refusal[0]} Focus this tab and try again.`;
  if (error.startsWith('Recording reached the sample limit')) {
    return 'Recording is too long. Record a shorter sequence.';
  }
  return error;
}

/** Minimal draft shape for the disruption meter; only `kind` and `steps` matter here. */
function emptyDraftFor(steps: JigStep[]): Draft {
  return {
    kind: PatternKind.MACRO,
    durationMs: 0,
    bootDelayMs: 5000,
    amplitude: 0,
    periodMs: 0,
    jitter: 0,
    seed: 0,
    flags: 0,
    steps,
  };
}
